import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import dayjs from 'dayjs';

const { TextArea } = Input;

interface Coupon {
    coupon_id?: number;
    coupon_code: string;
    coupon_amount: number;
    coupon_max_count: number;
    coupon_expire_date: string;
    coupon_description: string;
}

interface CouponModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: Coupon | null;
    isEditMode?: boolean;
}

const CouponModal: React.FC<CouponModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Handle form submit
    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const dataVal = {
                ...values,
                coupon_expire_date: values.coupon_expire_date ? values.coupon_expire_date.format('YYYY-MM-DD') : null,
                ...(isEditMode && initialValues?.coupon_id && { coupon_id: initialValues.coupon_id }),
            };

            const url = isEditMode ? '/edit-coupon' : '/add-coupon';
            const method = isEditMode ? 'put' : 'post';

            await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(dataVal);
            showSuccess(isEditMode ? 'Coupon updated successfully!' : 'Coupon created successfully!');
            form.resetFields();
            onCancel();
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} coupon`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                ...initialValues,
                coupon_expire_date: initialValues.coupon_expire_date ? dayjs(initialValues.coupon_expire_date) : null,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Coupon' : 'Add New Coupon'}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="coupon_code"
                            label="Coupon Code"
                            rules={[
                                { required: true, message: 'Please enter the coupon code' },
                                { pattern: /^[A-Z0-9_-]+$/, message: 'Use only uppercase letters, numbers, hyphens, or underscores' },
                                { min: 3, message: 'Minimum 3 characters required' },
                                { max: 15, message: 'Maximum 15 characters allowed' }
                            ]}
                        >
                            <Input
                                placeholder="e.g., SAVE100"
                                showCount
                                maxLength={15}
                                style={{ textTransform: 'uppercase' }}
                                onChange={(e) => form.setFieldsValue({ coupon_code: e.target.value.toUpperCase() })}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="coupon_amount"
                            label="Discount Amount (₹)"
                            rules={[
                                { required: true, message: 'Please enter the amount' },
                                { type: 'number', min: 1, message: 'Amount must be at least 1' }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="e.g., 100"
                                min={1}
                                precision={0}
                                onKeyDown={(e) => {
                                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="coupon_max_count"
                            label="Usage Limit (Count)"
                            rules={[
                                { required: true, message: 'Please enter usage limit' },
                                { type: 'number', min: 1, message: 'Limit must be at least 1' }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Max number of uses"
                                min={1}
                                step={1}
                                precision={0}
                                onKeyDown={(e) => {
                                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="coupon_expire_date"
                            label="Expiry Date"
                            rules={[{ required: true, message: 'Please select an expiry date' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD-MM-YYYY"
                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="coupon_description"
                    label="Description (Optional)"
                    rules={[{ max: 200, message: 'Description cannot exceed 200 characters' }]}
                >
                    <TextArea rows={3} placeholder="Provide details about this coupon..." maxLength={200} showCount />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            {isEditMode ? 'Update Coupon' : 'Create Coupon'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CouponModal;
